import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const FooterAdmin = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const { user } = useSelector((state) => state.user);
    const canEdit = user.role === "admin" || user.adminRole?.permissions?.footer?.edit;

    const [footerCompanyName, setFooterCompanyName] = useState("");
    const [footerPaymentNote, setFooterPaymentNote] = useState("");
    const [footerPaymentNoteZh, setFooterPaymentNoteZh] = useState("");
    const [utilityLinks, setUtilityLinks] = useState([]);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data } = await axios.get('/api/v1/site-settings');
                const s = data.settings;
                setFooterCompanyName(s.footerCompanyName || "1313 Mart");
                setFooterPaymentNote(s.footerPaymentNote || "Cash on Delivery available");
                setFooterPaymentNoteZh(s.footerPaymentNoteZh || "");
                setUtilityLinks(s.footerUtilityLinks && s.footerUtilityLinks.length > 0 ? s.footerUtilityLinks : []);
            } catch (error) {
                enqueueSnackbar("Failed to load footer settings", { variant: "error" });
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [enqueueSnackbar]);

    const updateLink = (index, field, value) => {
        setUtilityLinks((prev) => prev.map((link, i) => i === index ? { ...link, [field]: value } : link));
    };

    const addLink = () => {
        setUtilityLinks((prev) => [...prev, { label: "", labelZh: "", url: "" }]);
    };

    const removeLink = (index) => {
        setUtilityLinks((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put('/api/v1/admin/site-settings', {
                footerCompanyName,
                footerPaymentNote,
                footerPaymentNoteZh,
                footerUtilityLinks: utilityLinks,
            });
            enqueueSnackbar("Footer settings updated", { variant: "success" });
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title="Admin: Footer | 1313 Mart" />
            {saving && <BackdropLoader />}

            <div className="flex justify-between items-center mb-4">
                <div>
                    <Link to="/admin/site-content" className="text-sm text-primary-blue hover:underline">&larr; Back to Site Content</Link>
                    <h1 className="text-lg font-medium uppercase mt-1">Footer</h1>
                </div>
                <Link to="/admin/footer-columns" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">Manage Footer Columns</Link>
            </div>

            <div className="flex flex-col gap-6 bg-white rounded-lg shadow p-4 sm:p-6">

                <h2 className="font-medium text-lg">Bottom Bar</h2>
                <div className="flex flex-col sm:flex-row gap-4">
                    <TextField
                        label="Company Name (used in copyright line)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={footerCompanyName}
                        onChange={(e) => setFooterCompanyName(e.target.value)}
                    />
                    <TextField
                        label="Payment Note"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={footerPaymentNote}
                        onChange={(e) => setFooterPaymentNote(e.target.value)}
                        helperText="Shown instead of card logos — keep it accurate to what you actually accept"
                    />
                    <TextField
                        label="Payment Note (中文 — optional)"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={footerPaymentNoteZh}
                        onChange={(e) => setFooterPaymentNoteZh(e.target.value)}
                    />
                </div>

                <div>
                    <p className="text-sm text-gray-600 mb-2">Bottom Bar Links (e.g. "Sell on 1313 Mart", "Help Center")</p>
                    <div className="flex flex-col gap-3">
                        {utilityLinks.map((link, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <TextField
                                    label="Label"
                                    variant="outlined"
                                    size="small"
                                    value={link.label}
                                    onChange={(e) => updateLink(i, "label", e.target.value)}
                                />
                                <TextField
                                    label="Label (中文)"
                                    variant="outlined"
                                    size="small"
                                    value={link.labelZh || ""}
                                    onChange={(e) => updateLink(i, "labelZh", e.target.value)}
                                />
                                <TextField
                                    label="URL"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={link.url}
                                    onChange={(e) => updateLink(i, "url", e.target.value)}
                                />
                                {canEdit && (
                                    <button type="button" onClick={() => removeLink(i)} className="text-red-500 hover:text-red-700 p-2">
                                        <DeleteIcon />
                                    </button>
                                )}
                            </div>
                        ))}
                        {canEdit && (
                            <button type="button" onClick={addLink} className="flex items-center gap-1 text-sm text-primary-blue hover:underline w-fit">
                                <AddIcon sx={{ fontSize: "18px" }} /> Add Link
                            </button>
                        )}
                    </div>
                </div>

                {canEdit && (
                    <div className="flex justify-end">
                        <button onClick={handleSave} className="bg-primary-orange uppercase px-6 py-3 text-white font-medium rounded shadow hover:shadow-lg">
                            Save Changes
                        </button>
                    </div>
                )}

            </div>
        </>
    );
};

export default FooterAdmin;
